import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdAlert } from '@commander-services/gui-components';
import { useRecoilValue } from 'recoil';
import DynamicTable from '../DynamicTable';
import Form from './Form';
import ApiService from '../../Services/ApiService';
import { TABLE_ONLINE_MAP_ASSIGN, TABLE_TEMPLATES } from '../../Services/TableService';
import LoaderService from '../../Services/LoaderService';
import { tabsByPermission } from '.';
import TabComponent from '../TabComponent';
import { ITableFilter, setTableFilterAtom, tablesAtom } from '../../store/recoil/TableState';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { IGroupSelectOptions } from '../Forms/CmdField';
import OnlineHelp from '../OnlineHelp';
import { IVehicles } from '../Vehicles/types';
import { IDynamicTableState } from '../../interfaces/tables';
import { vehiclesAtom } from '../../store/recoil/vehicles';
import { selectedCustomersAtom } from '../../store/recoil/customers';

export const ONLINE_PANEL_ASSIGN_URL = '/online_panel/assign';

function OnlinePanelAssign() {
  const { formatMessage: f } = useIntl();
  const tables = useRecoilValue<IDynamicTableState>(tablesAtom);
  const tableFilter = useRecoilValue<ITableFilter | null>(setTableFilterAtom);
  const vehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const [isDefault, setIsDefault] = React.useState<boolean>(false);
  const [reloadTable, setReloadTable] = React.useState<boolean>(false);
  const [templateId, setTemplateId] = React.useState<number | string>('');
  const [vehicleIds, setVehicleIds] = React.useState<number[]>([]);
  const [vehicleName, setVehicleName] = React.useState<string>('');
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [isAvailableVehicleList, setIsAvailableVehicleList] = React.useState<boolean | null>(null);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const tabs = React.useState(
    tabsByPermission(
      [TABLE_TEMPLATES, TABLE_ONLINE_MAP_ASSIGN],
      TABLE_ONLINE_MAP_ASSIGN,
      selectedCustomers
    )
  )[0];
  const [availableTemplates, setAvailableTemplates] = React.useState<IGroupSelectOptions | null>(
    null
  );
  const documentTitle = `${f({ id: 'tab.online-map/assign' })} - ${f({
    id: 'menu.onlinePanel',
  })} - ${f({ id: 'menu.settings' })}`;
  useDocumentTitle(documentTitle);

  const handleClose = () => {
    setIsModalOpened(false);
  };

  const handleReloadTable = () => {
    setReloadTable(true);
  };

  const getTemplates = async (ids: number[], isDefaultValue: boolean) => {
    const apiUrl = `/v1/online-map/assign/available-templates`;
    const inputs = {
      vehicleIds: ids,
      default: isDefaultValue,
    };
    LoaderService.showLoader(true);
    const response = await ApiService.post(apiUrl, inputs);
    if (response.data && response.status === 200) {
      const optionsData = response.data;
      setIsModalOpened(true);
      setAvailableTemplates(optionsData);
    }
    LoaderService.showLoader(false);
  };

  const handleModal = (
    id: number,
    customer: number,
    type: string,
    name: string,
    template: number
  ) => {
    if (id) {
      setVehicleIds([id]);
      setVehicleName(name);
      setCustomerId(customer || null);
      setReloadTable(false);
      setIsDefault(type === 'action_assignDefault');
      getTemplates([id], type === 'action_assignDefault');
      setTemplateId(template);
    }
  };

  React.useEffect(() => {
    if (tables && isAvailableVehicleList === null) {
      const vehcilesTableData = tables[TABLE_ONLINE_MAP_ASSIGN];
      if (vehcilesTableData && Object.keys(vehicles).length === 0) {
        setIsAvailableVehicleList(true);
      }
    }
  }, [isAvailableVehicleList, tables, vehicles]);

  const handleAlertClose = () => {
    setIsAvailableVehicleList(false);
  };

  return (
    <div>
      <div className="col-12" style={{ display: '' }}>
        <div className="row section page-head">
          <h4>
            {`${f({ id: 'menu.settings' })} / ${f({
              id: 'menu.onlinePanel',
            })}`}{' '}
            <OnlineHelp topic="online-panel" />
          </h4>
        </div>
        <TabComponent tabs={tabs}>
          <div>
            <DynamicTable
              name={TABLE_ONLINE_MAP_ASSIGN}
              updateTable={reloadTable}
              filter={tableFilter}
              reloadTable={!!JSON.stringify(tableFilter)}
              openForm={handleModal}
              mustClosed={true}
              actionsInMenu={true}
            />
            {isModalOpened && availableTemplates && (
              <div>
                <Form
                  closeCallback={handleClose}
                  vehicleIds={vehicleIds}
                  vehicleName={vehicleName}
                  updateTable={handleReloadTable}
                  default={isDefault}
                  customerIds={customerId}
                  availableTemplates={availableTemplates}
                  templateId={templateId}
                />
              </div>
            )}
          </div>
        </TabComponent>
      </div>
      {isAvailableVehicleList && (
        <CmdAlert
          id="novehicles-alert "
          data={{
            title: f({ id: 'toastr.warning' }),
            message: f({ id: 'onlinepanel.noVehicles' }),
            type: 'warning',
          }}
          isAbsolute={true}
          onCloseCallback={handleAlertClose}
        />
      )}
    </div>
  );
}

export default OnlinePanelAssign;
